import { useContext, useEffect, useRef } from 'react';
import { BlockerOptions, NavigationBlockerContext } from '../context/NavigationBlockerProvider';
import { generateUUID } from '../utils/utils';

/**
 * A custom hook to manage navigation blockers.
 *
 * This hook indicates whether navigation should be blocked. When `isDirty` is `true`,
 * users attempting to navigate away are prompted with a confirmation dialog.
 * It triggers an optional `onDiscard` callback during navigation discard actions,
 * either through in-app navigation or browser `unload` events (integrated with
 * TanStack Router's `useBlocker` functionality).
 *
 * @param {BlockerOptions} options - Configuration for the blocker.
 * @param {boolean} options.isDirty - Indicates whether navigation should be blocked due to unsaved changes.
 * @param {Function} [options.onDiscard] - Optional callback executed when navigation is discarded or page is unloaded.
 * This can include cleanup actions (e.g., canceling background tasks).
 * @param {Function} [options.onConfirm] - Optional callback executed when user confirms to save changes before navigation.
 */
export const useNavigationBlocker = ({ isDirty, onDiscard, onConfirm, modalContent }: BlockerOptions) => {
  const { setShouldBlockNavigation, clearNavigationBlocker } = useContext(NavigationBlockerContext);
  const key = useRef<string>(generateUUID());

  useEffect(() => {
    const currentKey = key.current;
    setShouldBlockNavigation(currentKey, { isDirty, onDiscard, onConfirm, modalContent });

    return () => {
      clearNavigationBlocker(currentKey);
    };
  }, [isDirty, onDiscard, onConfirm, modalContent, setShouldBlockNavigation, clearNavigationBlocker]);
};
